import { useEffect, useState, type ReactNode } from 'react';

import { cn } from '../../shared';
import { Button, OverlayScrollArea, TooltipProvider, type AppIconName } from '../../wrappers';
import { useBreakpoint } from '../use-breakpoint/use-breakpoint';
import { useSidebarCollapse } from './use-sidebar-collapse';

export interface NavItem {
  id: string;
  label: string;
  icon: AppIconName;
  active?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

interface SidebarBaseConfig {
  header?: ReactNode;
  footer?: ReactNode;
  defaultCollapsed?: boolean;
}

export type SidebarConfig = SidebarBaseConfig &
  ({ items: NavItem[]; groups?: never } | { groups: NavGroup[]; items?: never });

function sectionsOf(config: SidebarConfig): NavGroup[] {
  if ('groups' in config) return config.groups;
  return [{ id: '', label: '', items: config.items }];
}

function NavItemButton({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const shared = {
    disabled: item.disabled,
    onClick: item.onSelect,
    'aria-current': item.active ? ('page' as const) : undefined,
    // A stronger token than ghost's own hover:bg-accent, or active reads as hover.
    className: cn(
      item.active &&
        'bg-primary text-primary-foreground hover:bg-primary-hover hover:text-primary-foreground',
    ),
  };

  if (collapsed) {
    return <Button variant="ghost" size="icon" icon={item.icon} tooltip={item.label} {...shared} />;
  }
  return (
    <Button
      variant="ghost"
      leftIcon={item.icon}
      {...shared}
      className={cn('w-full justify-start', shared.className)}
    >
      {item.label}
    </Button>
  );
}

export function Sidebar(config: SidebarConfig) {
  const { header, footer, defaultCollapsed } = config;
  const { isMobile, isTablet } = useBreakpoint();
  const { collapsed: userCollapsed, toggle } = useSidebarCollapse(defaultCollapsed);
  const collapsed = !isMobile && (isTablet || userCollapsed);

  const [settled, setSettled] = useState(false);
  useEffect(() => {
    setSettled(true);
  }, []);

  if (isMobile) return null;

  return (
    <TooltipProvider>
      <div
        data-testid="sidebar"
        className={cn(
          'bg-card border-border flex h-full flex-col overflow-hidden border-r',
          settled && 'transition-[width] duration-normal',
          collapsed ? 'w-sidebar-collapsed' : 'w-panel-sm',
        )}
      >
        {header ? <div className="p-sidebar shrink-0">{header}</div> : null}
        <OverlayScrollArea
          orientation="vertical"
          className="min-h-0 flex-1"
          viewportClassName="p-sidebar gap-inline flex flex-col"
          verticalScrollbarClassName="py-sidebar"
        >
          {sectionsOf(config).map((group) => (
            <div key={group.id} className="gap-inline flex flex-col">
              {group.label && !collapsed ? (
                <h3 className="text-overline text-muted-foreground px-dropdown-item-x">
                  {group.label}
                </h3>
              ) : null}
              {group.items.map((item) => (
                <NavItemButton key={item.id} item={item} collapsed={collapsed} />
              ))}
            </div>
          ))}
        </OverlayScrollArea>
        <div className="p-sidebar shrink-0">
          {footer ??
            (collapsed ? (
              <Button
                variant="ghost"
                size="icon"
                icon="chevron-right"
                tooltip="Expand sidebar"
                onClick={toggle}
              />
            ) : (
              <Button
                variant="outline"
                leftIcon="chevron-left"
                onClick={toggle}
                className="w-full justify-start"
              >
                Collapse
              </Button>
            ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
